import clsx from 'clsx';
import { ComponentPropsWithoutRef, useEffect, useRef } from 'react';
import { Table } from 'react-bootstrap';
import { Column, Row, useTable } from 'react-table';
import { useInputSelectContext } from '../context';

type Props = {
	columns: Column<any>[];
	data: any[];
	className?: string;
	bodyRowProps?: ComponentPropsWithoutRef<'tr'>;
	headerRowProps?: ComponentPropsWithoutRef<'thead'>;
	onRowSelect: (row: any) => void;
	showHeader?: boolean;
};

const TableSelect = ({
	columns,
	data,
	headerRowProps,
	bodyRowProps,
	className,
	onRowSelect,
	showHeader = false,
	...props
}: Props) => {
	const { rows, prepareRow, headerGroups, getTableProps, getTableBodyProps } = useTable({
		columns,
		data,
	});

	const { options, selectedIndex, selectedOption, isLoading } = useInputSelectContext();

	const tableRef = useRef<HTMLTableElement>(null);

	useEffect(() => {
		if (!tableRef.current) return;

		const tableRect = tableRef.current.getBoundingClientRect();
		const rowSelect = tableRef.current.tBodies[0].children[selectedIndex];

		if (!rowSelect) return;

		const rowSelectRect = rowSelect.getBoundingClientRect();

		if (rowSelectRect.top > tableRect.top || rowSelectRect.bottom < tableRect.bottom) {
			rowSelect.scrollIntoView({
				block: 'nearest',
			});
		}
	}, [selectedIndex]);

	useEffect(() => {
		if (options.length && tableRef.current && !isLoading) {
			scrollToOption();
		}
	}, [options, tableRef, isLoading]);

	const isOptionSelect = (rowData: any) => {
		if (!selectedOption || !rowData) {
			return false;
		}

		return selectedOption.code === rowData.code;
	};

	const scrollToOption = () => {
		const tableElement = tableRef.current;

		if (!tableElement) {
			return;
		}

		const rows = Array.from(tableElement.tBodies[0].children);
		const selectedRow = rows.find((row) => row.classList.contains('row-selected'));

		if (selectedRow) {
			selectedRow.scrollIntoView({
				block: 'nearest',
			});
		}
	};

	return (
		<Table
			bordered
			hover
			className={clsx('spaces m-0 pb-8', className)}
			ref={tableRef}
			{...getTableProps()}
			{...props}
		>
			<thead
				{...headerRowProps}
				className={clsx({
					'd-none': !showHeader,
				})}
			>
				{headerGroups.map((headerGroup) => (
					<tr
						{...headerGroup.getHeaderGroupProps()}
						className={clsx(
							'gs-0 border border-y-none',
							'text-start fw-bolder fs-8 bg-pri'
						)}
					>
						{headerGroup.headers.map((column) => (
							<th
								{...column.getHeaderProps()}
								className="position-sticky top-0 z-index-1"
							>
								{column.render('Header')}
							</th>
						))}
					</tr>
				))}
			</thead>

			<tbody {...getTableBodyProps()} className="text-gray bg-white">
				{!!rows.length &&
					rows.map((row: Row) => {
						prepareRow(row);
						return (
							<tr
								{...row.getRowProps()}
								className={clsx('cursor-pointer', {
									'row-selected': isOptionSelect(row.original),
									'bg-light': selectedIndex === row.index,
									'bg-light-info': isOptionSelect(row.original),
								})}
								onClick={() => {
									onRowSelect(row.original);
								}}
								{...bodyRowProps}
							>
								{row.cells.map((cell) => {
									return (
										<td
											{...cell.getCellProps()}
											className="align-middle spaces py-4 px-8"
										>
											{cell.render('Cell')}
										</td>
									);
								})}
							</tr>
						);
					})}
			</tbody>
		</Table>
	);
};

export default TableSelect;
